import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { searchOnenote } from '../../utils/onenoteSearch';
import { gekoppeldeOneNoteIds, heeftMailNotitie, ontbrekendeBronnen } from '../../utils/koppelVoorstellen';
import { useProjectData } from '../ProjectOverview/ProjectDataContext';
import SourceSearchBox from '../Offertes/SourceSearchBox';
import {
  T, Icon, stitchDialogPaper, stitchGradientBtn, stitchOutlineBtn, stitchSectionLabel,
} from '../ProjectOverview/parts/stitch';

/**
 * De project-opstart-wizard: één checklist die bij een nieuw project/lead alles
 * goed zet — koppelingen (offerteregels, briefing, meetings, OneNote), de
 * debrief, de slimme taakgeneratie (verrijkt met paspoort + briefing) en de
 * fasen van dit project.
 *
 * Bewust een checklist en geen stappen-dwang: je ziet in één oogopslag wat er
 * al goed staat en wat aandacht vraagt; elke regel heeft zijn eigen actie.
 * OneNote wordt in de browser gezocht (delegated token — app-only kan niet);
 * het Teams-kanaal wordt op projectnummer-prefix gevonden en opent als deeplink.
 */
const StatusDot = ({ status }) => {
  const kleur = status === 'ok' ? '#059669' : status === 'let-op' ? '#d97706' : '#94a3b8';
  const icon = status === 'ok' ? 'check_circle' : status === 'let-op' ? 'error' : 'radio_button_unchecked';
  return <Icon name={icon} size={19} sx={{ color: kleur, flexShrink: 0 }} />;
};

function Regel({ status, titel, detail, actie }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 1,
      borderBottom: `1px solid ${T.surfaceLow}` }}>
      <StatusDot status={status} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 700, color: T.text }}>{titel}</Typography>
        {detail && (
          <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, whiteSpace: 'pre-wrap' }}>{detail}</Typography>
        )}
      </Box>
      {actie}
    </Box>
  );
}

const TIP_ACTIE = {
  briefing: 'Briefing uploaden',
  mail: 'Mail zoeken',
  meetings: 'Meetings koppelen',
  onenote: 'OneNote koppelen',
};

/**
 * 'Alles gekoppeld?' (wiki 26 sep): rustige, wegklikbare tip bovenin de wizard —
 * welke bronnen hangen nog niet aan het project, met per bron één actie. Niet
 * verplicht; de wizard werkt ook zonder.
 */
function KoppelTip({ ontbrekend, onActie, onWeg, bezig }) {
  if (!ontbrekend.length) return null;
  return (
    <Box data-testid="koppel-tip" sx={{ mb: 2, p: 1.5, borderRadius: 2, bgcolor: T.surfaceLow, position: 'relative' }}>
      <Box component="button" type="button" onClick={onWeg} aria-label="Tip wegklikken"
        sx={{ position: 'absolute', top: 6, right: 6, border: 0, bgcolor: 'transparent', p: 0.25, cursor: 'pointer',
          color: T.outlineVariant, display: 'inline-flex', '&:hover': { color: T.text } }}>
        <Icon name="close" size={16} />
      </Box>
      <Typography variant="body2" sx={{ fontFamily: T.fontBody, color: T.text, pr: 3 }}>
        Nog niet gekoppeld: {ontbrekend.map((b) => b.label).join(', ')}.
      </Typography>
      <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, display: 'block', mb: 0.75 }}>
        Hoe meer bronnen aan het project hangen, hoe beter de debrief en de taken worden. Niet verplicht.
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5 }}>
        {ontbrekend.map((b) => (
          <Box key={b.key} component="button" type="button" onClick={() => onActie(b.key)} disabled={bezig === b.key}
            sx={{ border: 0, bgcolor: 'transparent', p: 0, cursor: 'pointer', fontFamily: T.fontBody, fontSize: '0.8rem',
              fontWeight: 600, color: T.primary, '&:hover': { textDecoration: 'underline' } }}>
            {bezig === b.key ? 'Bezig…' : `${TIP_ACTIE[b.key]} →`}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function OpstartWizard({ open, onClose, project, onOpenDialog, onOpenFasen, onOpenPaneel, onDebriefResultaat }) {
  const projectRecId = project?.airtable_id || project?.id;
  const pnr = project?.fields?.Projectnummer || '';
  const pnaam = project?.fields?.Projectnaam || '';

  const [check, setCheck] = useState(null);
  const [onenote, setOnenote] = useState(null); // null = bezig, {count,error}
  const [debriefBusy, setDebriefBusy] = useState(false);
  const { notes, transcripts, notitieLokaalErbij } = useProjectData();
  const [mailZoeken, setMailZoeken] = useState(false);
  const [tipBezig, setTipBezig] = useState(null);
  const briefingRef = useRef(null);
  const tipSleutel = `opstartTip:${projectRecId}`;
  const [tipWeg, setTipWeg] = useState(() => {
    try { return localStorage.getItem(tipSleutel) === 'weg'; } catch { return false; }
  });

  const laad = useCallback(async () => {
    setCheck(null);
    setOnenote(null);
    authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/opstart-check`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setCheck)
      .catch(() => setCheck({ error: true }));
    // OneNote: in de browser, met het eigen Graph-token. Zoek op nummer én naam.
    (async () => {
      try {
        const [a, b] = await Promise.allSettled([
          pnr ? searchOnenote(pnr, { top: 5 }) : Promise.resolve([]),
          pnaam ? searchOnenote(pnaam.slice(0, 40), { top: 5 }) : Promise.resolve([]),
        ]);
        const items = [...(a.value || []), ...(b.value || [])];
        const uniek = [...new Map(items.map((i) => [i.id, i])).values()];
        setOnenote({ count: uniek.length, titels: uniek.slice(0, 3).map((i) => i.title) });
      } catch (e) { setOnenote({ count: 0, error: e.message }); }
    })();
  }, [projectRecId, pnr, pnaam]);
  useEffect(() => { if (open && projectRecId) laad(); }, [open, projectRecId, laad]);

  const ontbrekend = ontbrekendeBronnen(
    check && { ...check, meetings: { ...(check.meetings || {}), gekoppeld: Math.max(transcripts.length, check.meetings?.gekoppeld || 0) } },
    {
      onenote: onenote && { ...onenote, gekoppeld: gekoppeldeOneNoteIds(notes).size },
      // Mail telt alleen als hij aan het project is OPGESLAGEN (bevestigde mailkoppeling
      // of mail als projectnotitie) — niet op de persoonlijke zoektreffers.
      mail: check ? ((check.mail?.gekoppeld || 0) > 0 || heeftMailNotitie(notes)) : null,
    },
  );

  const tipWegklikken = () => {
    setTipWeg(true);
    try { localStorage.setItem(tipSleutel, 'weg'); } catch { /* prima */ }
  };

  const uploadBriefing = async (file) => {
    if (!file) return;
    setTipBezig('briefing');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const pr = await authFetch(`${BACKEND_URL}/Api/parse-attachment`, { method: 'POST', body: fd });
      if (!pr.ok) throw new Error((await pr.json().catch(() => ({}))).error || 'Kon het document niet lezen');
      const { text } = await pr.json();
      if (!String(text || '').trim()) throw new Error('Geen tekst gevonden in het document');
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/briefings`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tekst: text, bestandsnaam: file.name, bron: 'upload' }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      toast.success('Briefing bewaard bij dit project — de check staat in de fase Opstart & briefing');
      laad();
    } catch (e) { toast.error(`Briefing opslaan mislukt: ${e.message}`); }
    finally { setTipBezig(null); }
  };

  // Mail/notulen als projectnotitie — zelfde zoeker en opslag als 'E-mail koppelen' in de fase.
  const koppelMail = async (blok) => {
    const tekst = String(blok || '').trim();
    if (!tekst) return;
    const kop = (tekst.split('\n')[0] || '').replace(/^[—\s]+|[—\s]+$/g, '');
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-notes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: projectRecId, title: (kop || 'Gekoppelde e-mail').slice(0, 120), notes: tekst, source: 'Koppeling (mail/notulen)' }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      notitieLokaalErbij?.((await r.json().catch(() => ({})))?.record);
      toast.success('Gekoppeld als projectnotitie — staat bij Notities');
    } catch (e) { toast.error(`Koppelen mislukt: ${e.message}`); }
  };

  const tipActie = (key) => {
    if (key === 'briefing') briefingRef.current?.click();
    else if (key === 'mail') setMailZoeken((v) => !v);
    else { onOpenPaneel?.('meetings'); onClose(); } // meetings + OneNote: het koppelscherm met voorstellen
  };

  const genereerDebrief = async () => {
    if (debriefBusy) return;
    setDebriefBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/document`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: 'debrief' }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
      const d = await r.json();
      onDebriefResultaat({ titel: 'Debrief — gegenereerd door Fable 5', markdown: d.markdown || '' });
      onClose();
    } catch (e) { toast.error(`Debrief mislukt: ${e.message}`); }
    finally { setDebriefBusy(false); }
  };

  const c = check || {};
  const laden = check === null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: stitchDialogPaper }}>
      <DialogTitle sx={{ fontFamily: T.font, fontWeight: 800 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon name="rocket_launch" size={22} sx={{ color: T.tertiary }} />
          Project opstarten{pnr ? ` — ${pnr}` : ''}
        </Box>
        <Typography variant="caption" sx={{ display: 'block', fontFamily: T.fontBody, color: T.outline, fontWeight: 400 }}>
          Eén checklist: staat alles goed gekoppeld, en zet de basis (debrief, taken, fasen) in een paar klikken.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {!laden && !tipWeg && (
          <KoppelTip ontbrekend={ontbrekend} onActie={tipActie} onWeg={tipWegklikken} bezig={tipBezig} />
        )}
        <input ref={briefingRef} type="file" hidden accept=".pdf,.docx,.pptx,.txt,.md"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; uploadBriefing(f); }} />
        {mailZoeken && (
          <Box sx={{ mb: 2 }}>
            <SourceSearchBox onAddText={koppelMail} label="Zoek in e-mail, notulen & meetings (klantnaam of onderwerp)" />
          </Box>
        )}
        <Typography sx={{ ...stitchSectionLabel, mb: 0.5 }}>Koppelingen</Typography>
        {laden ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2, color: T.outline }}>
            <CircularProgress size={16} />
            <Typography variant="caption" sx={{ fontFamily: T.fontBody }}>Koppelingen controleren…</Typography>
          </Box>
        ) : (
          <>
            <Regel
              status={(c.offerteregels?.count || 0) > 0 ? 'ok' : 'let-op'}
              titel="Offerteregels"
              detail={(c.offerteregels?.count || 0) > 0
                ? `${c.offerteregels.count} regels gekoppeld (€ ${Math.round(c.offerteregels.total || 0).toLocaleString('nl-NL')})`
                : 'Geen regels aan dit projectnummer — accepteer/koppel de offerte in Gripp, dan stroomt het vanzelf binnen.'}
            />
            <Regel
              status={c.briefing?.gekoppeld ? 'ok' : 'let-op'}
              titel="Briefing"
              detail={c.briefing?.uploads > 0
                ? `${c.briefing.uploads} klantbriefing(en) bewaard bij dit project.`
                : c.briefing?.gekoppeld
                ? 'Briefing gekoppeld — de fasen tonen hun eigen velden.'
                : 'Geen briefing aan dit project gekoppeld (veld "Project" op de briefing in Airtable).'}
            />
            <Regel
              status={c.paspoort?.gevuld ? 'ok' : 'let-op'}
              titel="Klantpaspoort"
              detail={c.paspoort?.gevuld
                ? 'Profiel gevuld — generatoren nemen tone-of-voice mee.'
                : 'Nog geen klantprofiel — vul het aan in de Account Workspace.'}
            />
            <Regel
              status={transcripts.length > 0 ? 'ok' : 'leeg'}
              titel="Meetings"
              detail={transcripts.length > 0
                ? `${transcripts.length} meeting(s) gekoppeld aan dit project.`
                : (c.meetings?.count || 0) > 0
                ? `${c.meetings.count} meeting(s) gevonden, nog niet gekoppeld: ${(c.meetings.titels || []).join(' · ')}`
                : 'Nog geen meetings gevonden op nummer/naam.'}
            />
            <Regel
              status={onenote === null ? 'leeg' : (onenote.count > 0 ? 'ok' : 'leeg')}
              titel="OneNote-aantekeningen"
              detail={onenote === null ? 'Zoeken…'
                : onenote.error ? `Zoeken mislukt: ${onenote.error}`
                : onenote.count > 0 ? `${onenote.count} pagina('s): ${(onenote.titels || []).join(' · ')}`
                : 'Niets gevonden in het team-notebook.'}
            />
            <Regel
              status={c.teams?.found ? 'ok' : 'let-op'}
              titel="Teams-kanaal"
              detail={c.teams?.found
                ? `${c.teams.team_name} › ${c.teams.channel_name}`
                : c.teams?.error
                  ? `Kanaal zoeken mislukt: ${c.teams.error}`
                  : `Geen kanaal gevonden dat met ${pnr || 'het projectnummer'} begint — maak het aan in het klant-team ("${pnr || '<nummer>'}. <initialen PL> - <naam>").`}
              actie={c.teams?.found && c.teams.web_url ? (
                <Box component="a" href={c.teams.web_url} target="_blank" rel="noreferrer"
                  sx={{ ...stitchOutlineBtn, fontSize: '0.75rem', flexShrink: 0, textDecoration: 'none' }}>
                  <Icon name="open_in_new" size={14} /> Open
                </Box>
              ) : null}
            />
          </>
        )}

        <Typography sx={{ ...stitchSectionLabel, mt: 2, mb: 0.5 }}>Basis neerzetten</Typography>
        <Regel status="leeg" titel="Debrief genereren"
          detail="Fable 5 vat de briefing samen tot afspraken, scope en open punten — bewaar hem als projectnotitie."
          actie={(
            <Box component="span" onClick={genereerDebrief} sx={{ ...stitchOutlineBtn, fontSize: '0.75rem', flexShrink: 0 }}>
              {debriefBusy ? <CircularProgress size={13} /> : <Icon name="auto_awesome" size={14} />} Genereer
            </Box>
          )} />
        <Regel status="leeg" titel="Slimme taken uit de offerte"
          detail="AI-voorstel uit de offerteregels, verrijkt met klantpaspoort en briefing — jij keurt en past aan."
          actie={(
            <Box component="span" onClick={() => { onOpenDialog('smartTasks'); onClose(); }}
              sx={{ ...stitchOutlineBtn, fontSize: '0.75rem', flexShrink: 0 }}>
              <Icon name="task_alt" size={14} /> Open
            </Box>
          )} />
        <Regel status="leeg" titel="Fasen van dit project"
          detail="Zet fasen uit die hier niet spelen (bv. geen postproductie) — de balk wordt korter."
          actie={(
            <Box component="span" onClick={() => { onOpenFasen(); onClose(); }}
              sx={{ ...stitchOutlineBtn, fontSize: '0.75rem', flexShrink: 0 }}>
              <Icon name="tune" size={14} /> Instellen
            </Box>
          )} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Chip size="small" label="Tip: de briefing-check (upload/plak) staat in de fase Opstart & briefing"
          sx={{ mr: 'auto', height: 22, fontSize: 11, bgcolor: T.surfaceLow, color: T.outline }} />
        <Box component="span" onClick={onClose} sx={stitchGradientBtn}>Klaar</Box>
      </DialogActions>
    </Dialog>
  );
}

export default OpstartWizard;
